import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import { Box, IconButton, Tooltip, Menu, MenuItem, Chip, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    FormatStrikethrough,
    FormatListBulleted,
    FormatListNumbered,
    FormatAlignLeft,
    FormatAlignCenter,
    FormatAlignRight,
    FormatAlignJustify,
    Undo,
    Redo,
    FormatClear,
    Title,
    Link as LinkIcon,
    Palette,
    FormatSize
} from '@mui/icons-material';

const TipTapEditor = ({
    value,
    onChange,
    placeholder,
    direction = 'ltr',
    editable = true,
    compact = false,
    minHeight = 120,
    maxHeight = 300,
}) => {
    const { t } = useTranslation();
    const [isRTL, setIsRTL] = useState(direction === 'rtl');
    const [colorMenuAnchor, setColorMenuAnchor] = useState(null);
    const [linkMenuAnchor, setLinkMenuAnchor] = useState(null);
    const [linkUrl, setLinkUrl] = useState('');

    const editor = useEditor({
        editable,
        extensions: [
            StarterKit.configure({
                history: false, // Disable default history
            }),

            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Color,
            TextStyle,
            Underline,
            Strike,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-link',
                },
            }),
        ],
        content: value || '',
        onUpdate: ({ editor }) => {
            const content = editor.getHTML();
            onChange(content);
            
            // Auto-detect RTL based on content
            const text = editor.getText();
            const rtlChars = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
            const detectedRTL = rtlChars.test(text);
            setIsRTL(detectedRTL);
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
                style: `direction: ${isRTL ? 'rtl' : 'ltr'}; text-align: ${isRTL ? 'right' : 'left'};`,
            },
            handleKeyDown: (view, event) => {
                // Handle Tab key for list indentation (Word-like: Tab indents, Shift+Tab outdents)
                if (event.key === 'Tab') {
                    const { state } = view;
                    const { selection } = state;

                    // Check if cursor is inside a list item (traverse ancestors - parent of paragraph is listItem)
                    const isInListItem = (() => {
                        for (let d = selection.$from.depth; d > 0; d--) {
                            if (selection.$from.node(d).type.name === 'listItem') return true;
                        }
                        return false;
                    })();

                    if (isInListItem) {
                        event.preventDefault();
                        event.stopPropagation();
                        if (event.shiftKey) {
                            editor.chain().focus().liftListItem('listItem').run();
                        } else {
                            editor.chain().focus().sinkListItem('listItem').run();
                        }
                        return true; // Handled - prevent Tab from moving focus to next element
                    }
                }

                // Handle Enter key for list continuation
                if (event.key === 'Enter') {
                    const { state } = view;
                    const { selection } = state;
                    const isInListItem = (() => {
                        for (let d = selection.$from.depth; d > 0; d--) {
                            if (selection.$from.node(d).type.name === 'listItem') return true;
                        }
                        return false;
                    })();

                    if (isInListItem) {
                        return false; // Allow default Enter behavior for lists
                    }
                }

                return false;
            },
        },
    });

    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value || '');
        }
    }, [editor, value]);

    useEffect(() => {
        if (editor) {
            editor.commands.setContent(value || '');
        }
    }, [editor]);

    useEffect(() => {
        if (editor) {
            editor.setEditable(editable);
        }
    }, [editor, editable]);

    if (!editor) {
        return null;
    }

    const colors = [
        '#000000', '#374151', '#6B7280', '#9CA3AF', '#D1D5DB',
        '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16',
        '#22C55E', '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9',
        '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#D946EF',
        '#EC4899', '#F43F5E'
    ];

    const handleColorSelect = (color) => {
        editor.chain().focus().setColor(color).run();
        setColorMenuAnchor(null);
    };

    const handleLinkAdd = () => {
        if (linkUrl) {
            editor.chain().focus().setLink({ href: linkUrl }).run();
        }
        setLinkMenuAnchor(null);
        setLinkUrl('');
    };

    const MenuButton = ({ onClick, isActive, children, title, variant = 'default' }) => (
        <Tooltip title={title}>
            <IconButton
                onClick={onClick}
                size="small"
                color={isActive ? 'primary' : 'default'}
                variant={variant}
                sx={{ 
                    mx: compact ? 0.15 : 0.3,
                    minWidth: compact ? 28 : 32,
                    height: compact ? 28 : 32,
                    borderRadius: 1,
                    '&:hover': { 
                        backgroundColor: isActive ? 'primary.light' : 'rgba(0, 0, 0, 0.04)',
                        transform: 'scale(1.05)'
                    },
                    transition: 'all 0.2s ease-in-out'
                }}
            >
                {children}
            </IconButton>
        </Tooltip>
    );

    return (
        <Box sx={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 2,
            overflow: 'hidden',
            direction: isRTL ? 'rtl' : 'ltr',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            backgroundColor: '#ffffff'
        }}>
            {/* Enhanced Toolbar - hidden when read-only */}
            {editable && (
            <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                p: compact ? 0.75 : 1.5,
                borderBottom: '1px solid #e0e0e0',
                background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                gap: compact ? 0.15 : 0.5,
                '& .MuiIconButton-root': {
                  padding: compact ? '3px' : undefined,
                },
            }}>
                {/* Headers */}
                <MenuButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    isActive={editor.isActive('heading', { level: 1 })}
                    title={t('Heading1')}
                >
                    <Title fontSize="small" />
                </MenuButton>

                <MenuButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    isActive={editor.isActive('heading', { level: 2 })}
                    title={t('Heading2')}
                >
                    <FormatSize fontSize="small" />
                </MenuButton>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                {/* Text Formatting */}
                <MenuButton
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    isActive={editor.isActive('bold')}
                    title={t('Bold')}
                >
                    <FormatBold fontSize="small" />
                </MenuButton>

                <MenuButton
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    isActive={editor.isActive('italic')}
                    title={t('Italic')}
                >
                    <FormatItalic fontSize="small" />
                </MenuButton>

                <MenuButton
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    isActive={editor.isActive('underline')}
                    title={t('Underline')}
                >
                    <FormatUnderlined fontSize="small" />
                </MenuButton>

                <MenuButton
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    isActive={editor.isActive('strike')}
                    title={t('Strikethrough')}
                >
                    <FormatStrikethrough fontSize="small" />
                </MenuButton>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                {/* Color Picker */}
                <MenuButton
                    onClick={(e) => setColorMenuAnchor(e.currentTarget)}
                    title={t('TextColor')}
                >
                    <Palette fontSize="small" />
                </MenuButton>

                {/* Lists */}
                <MenuButton
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    isActive={editor.isActive('bulletList')}
                    title={t('BulletList')}
                >
                    <FormatListBulleted fontSize="small" />
                </MenuButton>

                <MenuButton
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    isActive={editor.isActive('orderedList')}
                    title={t('NumberedList')}
                >
                    <FormatListNumbered fontSize="small" />
                </MenuButton>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                {/* Alignment */}
                <MenuButton
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    isActive={editor.isActive({ textAlign: 'left' })}
                    title={t('AlignLeft')}
                >
                    <FormatAlignLeft fontSize="small" />
                </MenuButton>

                <MenuButton
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    isActive={editor.isActive({ textAlign: 'center' })}
                    title={t('AlignCenter')}
                >
                    <FormatAlignCenter fontSize="small" />
                </MenuButton>

                <MenuButton
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    isActive={editor.isActive({ textAlign: 'right' })}
                    title={t('AlignRight')}
                >
                    <FormatAlignRight fontSize="small" />
                </MenuButton>

                <MenuButton
                    onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                    isActive={editor.isActive({ textAlign: 'justify' })}
                    title={t('Justify')}
                >
                    <FormatAlignJustify fontSize="small" />
                </MenuButton>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                {/* Link */}
                <MenuButton
                    onClick={(e) => setLinkMenuAnchor(e.currentTarget)}
                    isActive={editor.isActive('link')}
                    title={t('AddLink')}
                >
                    <LinkIcon fontSize="small" />
                </MenuButton>

                {/* Actions */}
                <MenuButton
                    onClick={() => editor.chain().focus().undo().run()}
                    title={t('Undo')}
                >
                    <Undo fontSize="small" />
                </MenuButton>

                <MenuButton
                    onClick={() => editor.chain().focus().redo().run()}
                    title={t('Redo')}
                >
                    <Redo fontSize="small" />
                </MenuButton>

                <MenuButton
                    onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                    title={t('ClearFormatting')}
                >
                    <FormatClear fontSize="small" />
                </MenuButton>
            </Box>
            )}

            {/* Color Picker Menu */}
            <Menu
                anchorEl={colorMenuAnchor}
                open={Boolean(colorMenuAnchor)}
                onClose={() => setColorMenuAnchor(null)}
                PaperProps={{
                    sx: { 
                        p: 1,
                        maxWidth: 300,
                        '& .MuiMenuItem-root': { minHeight: 'auto', p: 0.5 }
                    }
                }}
            >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 1 }}>
                    {colors.map((color) => (
                        <Chip
                            key={color}
                            onClick={() => handleColorSelect(color)}
                            sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: color,
                                border: '1px solid #ccc',
                                cursor: 'pointer',
                                '&:hover': { transform: 'scale(1.1)' },
                                transition: 'transform 0.2s'
                            }}
                        />
                    ))}
                </Box>
            </Menu>

            {/* Link Menu */}
            <Menu
                anchorEl={linkMenuAnchor}
                open={Boolean(linkMenuAnchor)}
                onClose={() => setLinkMenuAnchor(null)}
                PaperProps={{
                    sx: { p: 2, minWidth: 300 }
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <input
                        type="url"
                        placeholder={t('EnterURL')}
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        style={{
                            padding: '8px 12px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <IconButton size="small" onClick={() => setLinkMenuAnchor(null)}>
                            {t('CancelButton')}
                        </IconButton>
                        <IconButton size="small" onClick={handleLinkAdd} color="primary">
                            {t('AddLinkButton')}
                        </IconButton>
                    </Box>
                </Box>
            </Menu>

            {/* Enhanced Editor Content */}
            <Box sx={{ 
                minHeight,
                maxHeight,
                overflow: 'auto',
                p: compact ? 1.25 : 2.5,
                px: compact ? 1.5 : 3,
                backgroundColor: '#ffffff',
                '& .ProseMirror': {
                    outline: 'none',
                    minHeight: compact ? 32 : Math.max(minHeight - 36, 40),
                    fontSize: compact ? '13px' : '14px',
                    lineHeight: compact ? 1.45 : 1.6,
                    padding: compact ? '0 4px' : '0 8px',
                    '& p.is-editor-empty:first-child::before': {
                        content: `"${placeholder || t('EnterDescription')}"`,
                        color: '#999',
                        float: isRTL ? 'right' : 'left',
                        pointerEvents: 'none',
                        height: 0
                    },
                    '& h1': {
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        margin: '0.5rem 0'
                    },
                    '& h2': {
                        fontSize: '1.25rem',
                        fontWeight: 'bold',
                        margin: '0.4rem 0'
                    },
                    '& ul, & ol': {
                        paddingInlineStart: '1.75rem',
                        paddingInlineEnd: '0.5rem',
                        marginInlineStart: '0.5rem',
                        marginInlineEnd: 0
                    },
                    '& a': {
                        color: '#1976d2',
                        textDecoration: 'underline',
                        '&:hover': {
                            color: '#1565c0'
                        }
                    }
                }
            }}>
                <EditorContent editor={editor} />
            </Box>
        </Box>
    );
};

export default TipTapEditor;
